import {
    Chip,
    DataTable,
    DataTableCell,
    DataTableCustomComponent,
    DataTableRow,
    Page,
    Spinner,
    Text
} from '@7shifts/sous-chef';
import { Company, MetaTemplate } from 'adminPanel/types';
import { useCompanyChannels } from '../hooks/useCompanyChannels';
import { useEffect, useState } from 'react';
import { getCompanyTemplates } from 'adminPanel/api/companies';

type Props = {
    company: Company;
};

const STATUS_THEME: Record<string, 'success' | 'warning' | 'danger' | 'info'> =
    {
        APPROVED: 'success',
        PENDING: 'warning',
        REJECTED: 'danger',
        PAUSED: 'info'
    };

const CompanyTemplatesPage = ({ company }: Props) => {
    const { channels, isLoading: isLoadingChannels } = useCompanyChannels(
        company.id
    );
    const officialChannels = channels.filter((c) => c.oficial);

    const [selectedWhatsappId, setSelectedWhatsappId] = useState<number | null>(
        null
    );
    const [templates, setTemplates] = useState<MetaTemplate[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (
            !isLoadingChannels &&
            officialChannels.length > 0 &&
            !selectedWhatsappId
        ) {
            setSelectedWhatsappId(officialChannels[0].id);
        }
    }, [isLoadingChannels, officialChannels.length]);

    useEffect(() => {
        if (!selectedWhatsappId) return;
        setIsLoading(true);
        setError(null);
        setTemplates([]);
        getCompanyTemplates(company.id, selectedWhatsappId)
            .then((res) => setTemplates(res?.data ?? []))
            .catch(() => setError('Não foi possível carregar os templates.'))
            .finally(() => setIsLoading(false));
    }, [selectedWhatsappId]);

    return (
        <Page
            title="Templates"
            breadcrumbs={{ label: 'Empresas', url: '../../' }}
        >
            {/* Channel selector */}
            {isLoadingChannels ? (
                <Spinner />
            ) : officialChannels.length === 0 ? (
                <p style={{ color: '#6c757d', fontSize: 14 }}>
                    Esta empresa não possui canais oficiais (Meta) conectados.
                </p>
            ) : (
                <div
                    style={{
                        display: 'flex',
                        gap: 8,
                        flexWrap: 'wrap',
                        marginBottom: 20
                    }}
                >
                    {officialChannels.map((ch) => (
                        <button
                            key={ch.id}
                            onClick={() => setSelectedWhatsappId(ch.id)}
                            style={{
                                padding: '6px 14px',
                                borderRadius: 20,
                                border: '1px solid',
                                borderColor:
                                    selectedWhatsappId === ch.id
                                        ? '#339af0'
                                        : '#dee2e6',
                                background:
                                    selectedWhatsappId === ch.id
                                        ? '#e7f5ff'
                                        : '#fff',
                                color:
                                    selectedWhatsappId === ch.id
                                        ? '#1971c2'
                                        : '#495057',
                                fontWeight:
                                    selectedWhatsappId === ch.id ? 600 : 400,
                                cursor: 'pointer',
                                fontSize: 13
                            }}
                        >
                            {ch.name}
                            {ch.actualNumber ? ` (${ch.actualNumber})` : ''}
                        </button>
                    ))}
                </div>
            )}

            {error && <p style={{ color: '#e03131', fontSize: 14 }}>{error}</p>}

            {!isLoadingChannels && officialChannels.length > 0 && (
                <>
                    <DataTable
                        items={templates}
                        isLoading={isLoading}
                        itemComponent={TemplateRow}
                        columns={[
                            { name: 'name', label: 'Nome' },
                            {
                                name: 'category',
                                label: 'Categoria',
                                size: 0.22
                            },
                            {
                                name: 'language',
                                label: 'Idioma',
                                size: 0.15
                            },
                            {
                                name: 'status',
                                label: 'Status',
                                size: 0.2
                            }
                        ]}
                    />
                    {!isLoading && templates.length > 0 && (
                        <Text as="caption" color="grey-300">
                            {templates.length} template(s)
                        </Text>
                    )}
                </>
            )}
        </Page>
    );
};

type TemplateRowProps = DataTableCustomComponent<MetaTemplate>;

const TemplateRow = ({ item }: TemplateRowProps) => (
    <DataTableRow>
        <DataTableCell columnIndex={0}>
            <span style={{ fontFamily: 'monospace', fontSize: 13 }}>
                {item.name}
            </span>
        </DataTableCell>
        <DataTableCell columnIndex={1}>{item.category}</DataTableCell>
        <DataTableCell columnIndex={2}>{item.language}</DataTableCell>
        <DataTableCell columnIndex={3}>
            <Chip theme={STATUS_THEME[item.status] ?? 'info'}>
                {item.status}
            </Chip>
        </DataTableCell>
    </DataTableRow>
);

export default CompanyTemplatesPage;
